import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { TradingViewChart } from '@/components/charts/TradingViewChart'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, AlertTriangle, CalendarDays, Loader2, Clock, CheckCircle2, XCircle } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'
import { apiCall } from '@/services/api'
import { toast } from 'sonner'

// ─── Subscription Banner ──────────────────────────────────────────────────────
const SubscriptionBanner = () => {
  const user = useAuthStore(s => s.user)
  const isAdmin = useAuthStore(s => s.isAdmin)

  if (isAdmin || !user?.subscription_status) return null

  // ── Abbonamento scaduto o inattivo
  if (user.subscription_status === 'expired' || user.subscription_status === 'inactive') {
    const expiredDate = user.subscription_end_date
      ? new Date(user.subscription_end_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })
      : null

    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-500/50 bg-red-500/10 text-red-300 mb-2">
        <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-bold text-red-300">Abbonamento scaduto</span>
          <span className="ml-2 text-sm">
            {expiredDate
              ? <>Il tuo abbonamento è scaduto il <span className="font-semibold text-red-200">{expiredDate}</span>.</>
              : <>Il tuo abbonamento non è attivo.</>
            }
            {' '}Rinnova ora per riprendere l'accesso a tutte le funzionalità della piattaforma.
          </span>
        </div>
        <a
          href="/pricing"
          className="flex-shrink-0 px-3 py-1.5 text-xs font-bold bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors whitespace-nowrap"
        >
          Rinnova abbonamento
        </a>
      </div>
    )
  }

  // ── Abbonamento attivo
  if (user.subscription_status !== 'active' || !user.subscription_end_date) return null

  const endDate = new Date(user.subscription_end_date)
  const now = new Date()
  const msLeft = endDate.getTime() - now.getTime()
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24))
  const formattedDate = endDate.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })

  if (daysLeft <= 0) return null

  const isUrgent = daysLeft <= 7

  if (isUrgent) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-orange-500/50 bg-orange-500/10 text-orange-300 mb-2">
        <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <span className="font-bold text-orange-300">Abbonamento in scadenza!</span>
          <span className="ml-2 text-sm">
            Il tuo abbonamento scade il <span className="font-semibold text-orange-200">{formattedDate}</span>.
            {' '}Mancano solo <span className="font-bold text-white">{daysLeft} {daysLeft === 1 ? 'giorno' : 'giorni'}</span> — rinnova subito per non perdere l'accesso alla piattaforma.
          </span>
        </div>
        <a
          href="/pricing"
          className="flex-shrink-0 px-3 py-1.5 text-xs font-bold bg-orange-500 hover:bg-orange-400 text-white rounded-lg transition-colors whitespace-nowrap"
        >
          Rinnova ora
        </a>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-blue-500/30 bg-blue-500/5 text-slate-400 mb-2">
      <CalendarDays className="w-4 h-4 text-blue-400 flex-shrink-0" />
      <span className="text-sm">
        Abbonamento attivo fino al{' '}
        <span className="font-semibold text-slate-300">{formattedDate}</span>
        {' '}—{' '}
        <span className="text-blue-400">{daysLeft} giorni rimanenti</span>
      </span>
    </div>
  )
}

interface NewsEvent {
  id: number
  title: string
  currency: string
  event_time: string
  impact: string
}

export const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { token } = useAuthStore()
  const isRestrictedUser = useAuthStore(state => state.user?.emergency_mode ?? false)
  const [highImpactNews, setHighImpactNews] = useState<NewsEvent[]>([])
  const [newsLoading, setNewsLoading] = useState(true)
  const [activatingSubscription, setActivatingSubscription] = useState(false)

  useEffect(() => {
    const activateSubscriptionWithRetry = async () => {
      const payment = searchParams.get('payment')
      const sessionId = searchParams.get('session_id')
      
      if (payment === 'success' && sessionId) {
        let authToken = token
        
        if (!authToken) {
          console.log('Token not available yet, waiting for auth store...')
          for (let waitAttempt = 0; waitAttempt < 10; waitAttempt++) {
            await new Promise(resolve => setTimeout(resolve, 500))
            authToken = useAuthStore.getState().token
            if (authToken) {
              console.log('Token available after wait')
              break
            }
          }
        }
        
        if (!authToken) {
          console.error('Token still not available after waiting')
          toast.error('Sessione scaduta. Effettua nuovamente il login.')
          setSearchParams({})
          return
        }
        
        setActivatingSubscription(true)
        const maxRetries = 5
        const delays = [2000, 4000, 6000, 8000, 10000]
        
        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            if (attempt > 0) {
              toast.loading(`Attivazione in corso... (tentativo ${attempt + 1}/${maxRetries})`, { id: 'activation' })
            } else {
              toast.loading('Attivazione abbonamento in corso...', { id: 'activation' })
            }
            
            const response = await fetch(`/api/v1/payments/subscription/success?session_id=${sessionId}`, {
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            })
            
            if (response.ok) {
              toast.dismiss('activation')
              const planId = searchParams.get('plan')
              if (planId === 'activation') {
                toast.success('Piattaforma attivata con successo!')
                useAuthStore.getState().updateUser({ subscription_status: 'active', has_paid_initial_fee: true })
              } else {
                toast.success('Abbonamento rinnovato con successo!')
                useAuthStore.getState().updateUser({ subscription_status: 'active' })
              }
              setSearchParams({})
              setActivatingSubscription(false)
              setTimeout(() => window.location.reload(), 1500)
              return
            } else {
              const error = await response.json()
              if (attempt === maxRetries - 1) {
                toast.dismiss('activation')
                toast.error(error.detail || 'Errore attivazione. Contatta il supporto.')
              }
            }
          } catch (err) {
            console.error(`Subscription activation attempt ${attempt + 1} failed:`, err)
            if (attempt === maxRetries - 1) {
              toast.dismiss('activation')
              toast.error('Server non raggiungibile. Riprova tra qualche minuto o contatta il supporto.')
            }
          }
          
          if (attempt < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, delays[attempt]))
          }
        }
        
        setSearchParams({})
        setActivatingSubscription(false)
      }
    }
    
    activateSubscriptionWithRetry()
  }, [searchParams, token, setSearchParams])

  useEffect(() => {
    const fetchHighImpactNews = async () => {
      try {
        const news = await apiCall<NewsEvent[]>('/news/today-high-impact')
        setHighImpactNews(news)
      } catch (error) {
        console.error('Error fetching high impact news:', error)
      } finally {
        setNewsLoading(false)
      }
    }
    
    fetchHighImpactNews()
  }, [])

  return (
    <div className="p-6 space-y-6">
      {/* Subscription Banner */}
      <SubscriptionBanner />

      {/* Main Grid: TradingView + Account Cards */}
      <div className="grid grid-cols-12 gap-6 items-start">
        {/* TradingView Chart - Left Column */}
        <div className={isRestrictedUser ? "col-span-12" : "col-span-8"}>
          <TradingViewChart symbol="XAUUSD" theme="dark" height={600} />
        </div>
        
        {/* Account Cards - Right Column - Hidden for restricted user */}
        {!isRestrictedUser && (
          <div className="col-span-4 space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  News High Impact Oggi
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {newsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : highImpactNews.length === 0 ? (
                  <div className="flex items-center space-x-2 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-sm text-emerald-400">Nessuna news high impact oggi</span>
                  </div>
                ) : (
                  highImpactNews.map((news) => {
                    const eventTime = new Date(news.event_time)
                    const now = new Date()
                    const isPast = eventTime < now
                    const timeStr = eventTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
                    
                    return (
                      <div 
                        key={news.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isPast 
                            ? 'bg-gray-500/10 border-gray-500/20' 
                            : 'bg-red-500/10 border-red-500/20'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Clock className={`w-4 h-4 flex-shrink-0 ${isPast ? 'text-gray-500' : 'text-red-500'}`} />
                          <span className={`text-sm truncate ${isPast ? 'text-gray-400 line-through' : 'text-foreground'}`}>
                            {news.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            news.currency === 'USD' ? 'bg-green-500/20 text-green-400' :
                            news.currency === 'AUD' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-orange-500/20 text-orange-400'
                          }`}>
                            {news.currency}
                          </span>
                          <span className={`text-xs font-mono ${isPast ? 'text-gray-500' : 'text-red-400'}`}>
                            {timeStr}
                          </span>
                        </div>
                      </div>
                    )
                  })
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
